import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { AgGridReact } from 'ag-grid-react';
import { useAuth } from '../../context/AuthContext';
import FillingNotesDialog from '../FillingNotesDialog';

import {
  API_URL_LANES_PRICING,
  API_URL_PRICES_UPDATE,
  API_URL_STATE_UPDATE,
  API_URL_TENDERS,
  API_URL_TENDERS_DELETE,
  API_URL_STATUS,
  API_BASE_URL,
  API_URL_TENDERS_EXPORT,
  API_URL_EDIT,
  tenderColumnsDefinitionOverview,
} from '../../configuration/Configuration';
import {
  isPosted,
  headerColumns,
  setGridColsFeedback,
} from '../../configuration/Utils';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import TableControls from '../../integrations/core/table/TableControls';
import TenderActionStatusCellRenderer from '../../integrations/core/table/cells/TenderActionStatusCellRenderer';
import {
  applySavedColumnLayout,
  getColumnLayoutScope,
  hasSavedColumnLayout,
  saveGridColumnLayout,
} from '../../integrations/core/table/columnLayoutPersistence';

let formData = {};
let formDataCustomerRel = {};
let formDataCustomerAbs = {};
let formDataLaneProperties = {};

const getOnewayMultiplier = (onewayValue) => {
  if (!onewayValue || onewayValue === '- no Mapping -') {
    return 1;
  }

  return String(onewayValue).toLowerCase() === 'roundtrip' ? 2 : 1;
};

const scaleRoundtripDependentFields = (laneData, oldOneway, newOneway) => {
  const oldMultiplier = getOnewayMultiplier(oldOneway);
  const newMultiplier = getOnewayMultiplier(newOneway);
  const factor = newMultiplier / oldMultiplier;

  if (factor === 1) {
    return [];
  }

  const fieldsToScale = [
    'duration',
    'distance_here',
    'distance_transporeon',
    'tolls',
    'suggested-price-total',
  ];
  const updatedFields = [];

  fieldsToScale.forEach((field) => {
    const currentValue = laneData[field];
    if (
      currentValue === null ||
      currentValue === undefined ||
      currentValue === ''
    ) {
      return;
    }

    const numericValue = Number(currentValue);
    if (!Number.isFinite(numericValue)) {
      return;
    }

    laneData[field] = numericValue * factor;
    updatedFields.push(field);
  });

  return updatedFields;
};

let gridOptions = {
  // Custom column names can contain dots (e.g. "Max. payload (in tons)").
  // Treat field names literally instead of dot-path object notation.
  suppressFieldDotNotation: true,
  rowClassRules: {
    'final-price-set': (params) => {
      const value = params?.data?.final;
      return value != null && !isNaN(value) && value > 0;
    },
    'partially-filled-lane': (params) => {
      const missing = params?.data?.missing;
      return missing && missing.trim() !== '';
    },
    'no-matching-lane': (params) => {
      return !!(params?.data?.origin_no_matching || params?.data?.destination_no_matching);
    },
  },
  // Enable automatic row class updates when data changes
  suppressRowHoverHighlight: false,
  animateRows: false, // Disable row animations to prevent navigation issues
};

const TENDER_STATUSES = [
  'Draft',
  'Round 1 Started',
  'Round 1 Prices',
  'Round 1 Finalized',
  'Round 2 Feedback',
  'Round 2 Prices',
  'Round 2 Finalized',
  'Round 3 Feedback',
  'Round 3 Prices',
  'Round 3 Finalized',
  'Closed',
  'Accepted',
  'Rejected_finance-department',
  'Rejected_legal-department',
  'Rejected_qsu-department',
];

const LanePricing = forwardRef((props, ref) => {
  const innerRef = useRef();
  let [searchParams, setSearchParams] = useSearchParams();

  const { key } = useLocation();
  const navigate = useNavigate();
  const { userType: user } = useAuth();
  const [contextMenu, setContextMenu] = useState(null);

  const handleCellContextMenu = (params) => {
    const event = params.event;
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      tender: params.data,
    });
  };

  const closeContextMenu = () => setContextMenu(null);

  const handleSetTenderStatus = async (tender, newStatus) => {
    closeContextMenu();
    try {
      await axios.post(
        API_URL_EDIT + 'tender',
        { [tender.id]: { tender_status: newStatus } },
        { withCredentials: true }
      );
      getTenderDataFiltered();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Failed to update tender status.';
      setStatusChangeError({ open: true, message: msg });
    }
  };

  useEffect(() => {
    handleUnload();
    getTenderDataFiltered();
  }, [key]);

  useEffect(() => {
    getTenderDataFiltered();
  }, [props.tender_input, user]);

  // Helper function to track tender views and check notes acknowledgment
  const trackTenderView = async (tenderID) => {
    try {
      const username = props.user;
      const response = await axios.post(
        `${API_BASE_URL}/api/tender/track-view/`,
        {
          tender_id: tenderID,
          username: username,
        }
      );

      // Check if user needs to acknowledge filling notes
      // Viewers see filling notes as read-only info — no acknowledgment required
      if (
        response.data.has_filling_notes &&
        !response.data.notes_acknowledged &&
        !user.isAdmin &&
        !user.isViewer
      ) {
        setFillingNotes(response.data.filling_notes);
        setNotesAcknowledged(false);
        setShowFillingNotesDialog(true);
      } else {
        setFillingNotes(response.data.filling_notes || '');
        setNotesAcknowledged(true);
      }

      return response.data;
    } catch (error) {
      console.error('Error tracking tender view:', error);
      return null;
    }
  };

  // Helper function to acknowledge filling notes
  const acknowledgeFillingNotes = async () => {
    try {
      const username = props.user;
      await axios.post(`${API_BASE_URL}/api/tender/acknowledge-notes/`, {
        tender_id: tenders,
        username: username,
      });

      setNotesAcknowledged(true);
      setShowFillingNotesDialog(false);
    } catch (error) {
      console.error('Error acknowledging filling notes:', error);
      alert('Error acknowledging notes. Please try again.');
    }
  };

  // Helper function to remove cell editing from column definitions
  const removeEditingFromColumns = (columns) => {
    return columns.map((col) => {
      const { cellEditor, cellEditorParams, ...colWithoutEditing } = col;
      return { ...colWithoutEditing, editable: false };
    });
  };

  const getTenderDataFiltered = async () => {
    let filterValue = '';
    if (user.isAdmin || user.isViewer) {
      filterValue = 'a';
    } else {
      filterValue = 'aR';
    }
    const data1 = (
      await axios.get(
        props.tender_input + 'filtered/' + filterValue + '/' + props.user
      )
    ).data;
    let cols = tenderColumnsDefinitionOverview;
    if (user.isStaff) {
      cols = [
        {
          colId: 'departmentOfferStatus',
          headerName: 'Offer Status',
          resizable: true,
          filter: true,
          sortable: true,
          minWidth: 160,
          valueGetter: (params) => {
            if (params.data?.department_rejected) {
              return 'Declined';
            }
            if (params.data?.regionalStatus) {
              return 'Prices Set';
            }
            return 'Open';
          },
          cellRendererFramework: TenderActionStatusCellRenderer,
        },
      ].concat(cols);
    }

    // Remove cell editing capabilities if disableCellEditing prop is true AND we're in overview mode
    if (props.disableCellEditing) {
      cols = removeEditingFromColumns(cols);
    }

    setTenderData1(data1);

    setOverviewGridCols(applySavedColumnLayout(cols, overviewLayoutScope));
  };

  const reset = () => {
    handleUnload();
    getTenderDataFiltered();
    clearTenderIdFromUrl();
  };

  // has tender ID been selected state
  const [tenderState, setTenderState] = useState(false);
  const [changesDone, setChangesDone] = useState(false);
  const [changesDoneStatus, setChangesDoneStatus] = useState(true);
  const [tenderStatus, setTenderStatus] = useState('');
  const [tenderData, setTenderData] = useState([]);
  const [tenderRequiredFields, setTenderRequiredFields] = useState([]);
  const [tenderData1, setTenderData1] = useState([]);
  const [tenders, setTender] = useState('');
  const overviewLayoutScope = getColumnLayoutScope();
  const detailLayoutScope = getColumnLayoutScope(tenders);
  const [pricesCompleted, setPricesCompleted] = useState(true);
  const [checked, setChecked] = useState('close0');
  const [isLoading, setIsLoading] = useState(false);
  const [company, setCompany] = useState('');
  const [requested_capacity, setRequestedCapacity] = useState('');
  const [tenderInformation, setTenderInformation] = useState('');
  const [checksComplete, setChecksComplete] = useState(true);
  const [internalDeadline, setInternalDeadline] = useState(null);
  const [isPastDeadline, setIsPastDeadline] = useState(false);
  const [requiredFieldsComplete, setRequiredFieldsComplete] = useState(true);
  const [missingRequiredDocuments, setMissingRequiredDocuments] = useState([]);

  // State for filling notes
  const [fillingNotes, setFillingNotes] = useState('');
  const [showFillingNotesDialog, setShowFillingNotesDialog] = useState(false);
  const [notesAcknowledged, setNotesAcknowledged] = useState(true);

  // State for price confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [declinedOfferNotification, setDeclinedOfferNotification] = useState({
    open: false,
    message: '',
  });
  const [statusChangeError, setStatusChangeError] = useState({
    open: false,
    message: '',
  });
  const [transitTimeValidationError, setTransitTimeValidationError] = useState({
    open: false,
    message: '',
  });

  const closeTransitTimeValidationError = () => {
    setTransitTimeValidationError((current) => ({
      ...current,
      open: false,
    }));
  };

  const handleTransitTimeValidationErrorClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    closeTransitTimeValidationError();
  };

  // Expose reset function to parent component via ref
  useImperativeHandle(ref, () => ({
    reset,
  }));

  // isFullscreen drives layout instead of a JS-computed pixel height.
  // When fullscreen is active the ag-theme div becomes a flex column that fills
  // the FullScreen wrapper (height: '100%'). The AgGridReact wrapper gets
  // flex: 1 / min-height: 0 so it takes the exact remaining space after the
  // TableControls toolbar — no pixel calculation or timing dependency needed.
  const [isFullscreen, setIsFullscreen] = useState(false);

  const gridRef1 = useRef();
  const gridRef2 = useRef();
  const gridRef3 = useRef();

  let fullScreenHandle1 = useFullScreenHandle();
  let fullScreenHandle2 = useFullScreenHandle();
  let fullScreenHandle3 = useFullScreenHandle();

  useEffect(() => {
    if (tenderData && tenderData.length > 0) {
      setRequiredFieldsComplete(validateRequiredFields());
    }
  }, [tenderData]);

  // Track fullscreen state via a direct DOM listener so isFullscreen is always
  // in sync with the browser regardless of react-full-screen's async onChange
  // chain (which can miss updates when handles remount between overview ↔ detail).
  useEffect(() => {
    const onFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFSChange);
    return () => document.removeEventListener('fullscreenchange', onFSChange);
  }, []);

  const [overviewGridCols, setOverviewGridCols] = useState([]);
  const [gridCols, setGridCols] = useState([]);
  const [gridColsF, setGridColsF] = useState([]);

  function autoSizeAll(gridApi, skipHeader) {
    const allColumnIds = [];
    gridApi.getColumns().forEach((column) => {
      allColumnIds.push(column.getId());
    });

    gridApi.autoSizeColumns(allColumnIds, skipHeader);
  }

  // Column defs load after the row data, so this also runs on column changes.
  // Wait a frame so React has rendered the new headers before measuring.
  const autoSizeGridColumns = useCallback((gridRef, layoutScope) => {
    window.requestAnimationFrame(() => {
      if (!gridRef?.current?.columnApi || hasSavedColumnLayout(layoutScope)) {
        return;
      }

      autoSizeAll(gridRef.current.columnApi, false);
    });
  }, []);

  const persistColumnLayout = useCallback((event, layoutScope) => {
    saveGridColumnLayout(event.columnApi, layoutScope);
  }, []);

  const persistColumnLayoutOnResize = useCallback((event, layoutScope) => {
    // Only save user resizes; saving auto-size output would block later auto-sizes
    if (!event.finished || event.source === 'autosizeColumns') {
      return;
    }

    saveGridColumnLayout(event.columnApi, layoutScope);
  }, []);

  // reset states before unload
  useEffect(() => {
    window.addEventListener('beforeunload', handleUnload);

    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  useEffect(() => {
    const handleDocumentsUpdated = (event) => {
      const updatedTenderId = event?.detail?.tenderId;
      if (updatedTenderId && String(updatedTenderId) === String(tenders)) {
        fetchRequiredDocumentStatus(updatedTenderId);
      }
    };

    window.addEventListener('tender-documents-updated', handleDocumentsUpdated);
    return () =>
      window.removeEventListener(
        'tender-documents-updated',
        handleDocumentsUpdated
      );
  }, [tenders, user]);

  const handleUnload = () => {
    setTenderState(false);
    setChangesDone(false);
    setChecksComplete(false);
    setChangesDoneStatus(false);
    setChecked('close0');
    setTenderInformation('');
    setMissingRequiredDocuments([]);
    setDeclinedOfferNotification({ open: false, message: '' });
  };

  const showDeclinedOfferNotification = (viewData) => {
    if (!viewData?.department_rejected || user.isAdmin) {
      setDeclinedOfferNotification({ open: false, message: '' });
      return;
    }

    const declinedBy =
      viewData.department_rejection_department || 'your department';
    const comment = viewData.department_rejection_comment?.trim();
    const message = comment
      ? `This tender was already declined by ${declinedBy}. Comment: ${comment}`
      : `This tender was already declined by ${declinedBy}.`;

    setDeclinedOfferNotification({
      open: true,
      message,
    });
  };

  const handleChangeSwitch = (event) => {
    setChecked(event.target.value);
  };

  const setUrlParams = (id) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('tender_id', id);
    setSearchParams(nextSearchParams);
  };

  const clearTenderIdFromUrl = () => {
    if (!searchParams.get('tender_id')) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('tender_id');
    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    const tenderId = searchParams.get('tender_id');

    if (!tenderId) {
      // Exit fullscreen before switching to overview mode. If a FullScreen
      // div unmounts while the browser auto-exits fullscreen, handles whose
      // node.current is null satisfy null===null and incorrectly become
      // active. Exiting here keeps all divs mounted (node.current non-null)
      // when fullscreenchange fires, so handles update correctly.
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {}).finally(() => handleUnload());
      } else {
        handleUnload();
      }
      return;
    }

    loadTenderData(tenderId);
  }, [searchParams]);

  // Exit fullscreen on unmount (cross-route navigation) to prevent the
  // browser from staying in fullscreen after the component is gone.
  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  // Helper function to calculate missing fields for a lane
  const getMissingFieldsForLane = (lane, requiredFields) => {
    if (!lane) return '';

    // Helper function to check if a field is filled
    const isFieldFilled = (field) => {
      return (
        field !== null &&
        field !== undefined &&
        field !== '' &&
        field !== '- no Mapping -'
      );
    };

    const regionalPrice = lane.regional;
    const capacity = lane.capacity;
    const transitTime = lane.transit_time;
    const additionalCosts = lane.additional_costs;
    const additionalDataY = lane.additionalData_y;

    const regionalFilled = isFieldFilled(regionalPrice);
    const capacityFilled = isFieldFilled(capacity);
    const transitTimeFilled = isFieldFilled(transitTime);
    const additionalCostsFilled = isFieldFilled(additionalCosts);
    const additionalDataYFilled = isFieldFilled(additionalDataY);

    // Build list of missing field names
    const missingFields = [];
    if (!regionalFilled) missingFields.push('price');
    if (requiredFields.includes('capacity') && !capacityFilled) {
      missingFields.push('capacity');
    }
    if (requiredFields.includes('transit_time') && !transitTimeFilled) {
      missingFields.push('transit time');
    }
    if (requiredFields.includes('additional_costs') && !additionalCostsFilled) {
      missingFields.push('additional costs');
    }
    if (requiredFields.includes('additional_data') && !additionalDataYFilled) {
      missingFields.push('additional data');
    }

    // Check required custom columns
    requiredFields.forEach((field) => {
      // Custom columns start with "custom_"
      if (field.startsWith('custom_')) {
        // Global custom columns now have "_mapped" suffix in the data
        const mappedField = `${field}_mapped`;

        let customColumnFilled = false;

        // For admin users: check the global _mapped field
        // For department users: ONLY check their department-specific field, not the global _mapped
        if (user.isAdmin) {
          // Admin: check mapped field
          if (isFieldFilled(lane[mappedField])) {
            customColumnFilled = true;
          }
        } else {
          // Department users: check department-specific field names (pattern: field_departmentName)
          // Do NOT check the _mapped field as it's read-only for them
          for (const laneKey in lane) {
            if (laneKey.startsWith(field + '_') && laneKey !== mappedField) {
              if (isFieldFilled(lane[laneKey])) {
                customColumnFilled = true;
                break;
              }
            }
          }
        }

        if (!customColumnFilled) {
          // Extract display name from field (remove "custom_" prefix and numeric prefix)
          const displayName = field.replace('custom_', '').replace(/^\d+_/, '');
          missingFields.push(displayName);
        }
      }
    });

    // Check if any fields are filled (to determine if lane is partially filled)
    let anyFieldFilled =
      regionalFilled ||
      (requiredFields.includes('capacity') && capacityFilled) ||
      (requiredFields.includes('transit_time') && transitTimeFilled) ||
      (requiredFields.includes('additional_costs') && additionalCostsFilled) ||
      (requiredFields.includes('additional_data') && additionalDataYFilled);

    // Also check if any custom columns are filled
    requiredFields.forEach((field) => {
      if (field.startsWith('custom_')) {
        // Global custom columns now have "_mapped" suffix in the data
        const mappedField = `${field}_mapped`;

        // For admin users: check the global _mapped field
        // For department users: ONLY check their department-specific field, not the global _mapped
        if (user.isAdmin) {
          // Admin: check mapped field
          if (isFieldFilled(lane[mappedField])) {
            anyFieldFilled = true;
          }
        } else {
          // Department users: check department-specific field names
          // Do NOT check the _mapped field as it's read-only for them
          for (const laneKey in lane) {
            if (laneKey.startsWith(field + '_') && laneKey !== mappedField) {
              if (isFieldFilled(lane[laneKey])) {
                anyFieldFilled = true;
                break;
              }
            }
          }
        }
      }
    });

    // Only return missing fields if lane is partially filled
    if (anyFieldFilled && missingFields.length > 0) {
      return missingFields.join(', ');
    }

    return '';
  };

  // Helper function to calculate missing fields for a lane (using state)
  const getMissingFields = (lane) => {
    return getMissingFieldsForLane(lane, tenderRequiredFields);
  };

  const getRequiredFieldLabel = (field) => {
    if (field === 'capacity') return 'capacity';
    if (field === 'transit_time') return 'transit time';
    if (field === 'additional_costs') return 'additional costs';
    if (field === 'additional_data') return 'additional data';
    if (field.startsWith('custom_')) {
      return field.replace('custom_', '').replace(/^\d+_/, '');
    }
    return field;
  };

  const getRequiredFieldsDescription = (requiredFields) => {
    const labels = [
      'regional price',
      ...requiredFields.map(getRequiredFieldLabel),
    ];
    return labels.join(', ');
  };

  // Helper function to apply missing field borders to all rows
  const applyMissingFieldBorders = () => {
    if (!gridRef1.current?.api) return;

    gridRef1.current.api.forEachNode((node) => {
      const rowElement = document.querySelector(
        `[row-index="${node.rowIndex}"]`
      );
      if (!rowElement) return;

      const missing = node.data?.missing || '';
      const isPartiallyFilled = missing && missing.trim() !== '';
      const missingFieldsList = missing
        .split(', ')
        .map((f) => f.trim())
        .filter((f) => f);

      // Get all cells in the row
      const allCells = rowElement.querySelectorAll('[col-id]');
      allCells.forEach((cellElement) => {
        const colId = cellElement.getAttribute('col-id');
        let shouldHaveBorder = false;

        if (isPartiallyFilled) {
          // Check standard required fields
          if (colId === 'regional' && missingFieldsList.includes('price')) {
            shouldHaveBorder = true;
          } else if (
            colId === 'capacity' &&
            missingFieldsList.includes('capacity')
          ) {
            shouldHaveBorder = true;
          } else if (
            colId === 'transit_time' &&
            missingFieldsList.includes('transit time')
          ) {
            shouldHaveBorder = true;
          } else if (
            colId === 'additional_costs' &&
            missingFieldsList.includes('additional costs')
          ) {
            shouldHaveBorder = true;
          } else if (
            colId === 'additionalData_y' &&
            missingFieldsList.includes('additional data')
          ) {
            shouldHaveBorder = true;
          }
          // Check custom columns
          else if (
            colId &&
            (colId.startsWith('custom_') || colId.includes('_mapped'))
          ) {
            let displayName = colId
              .replace('custom_', '')
              .replace(/_mapped$/, '')
              .replace(/^\d+_/, '');
            const parts = displayName.split('_');
            if (parts.length > 1) {
              const nameWithoutDept = parts.slice(0, -1).join('_');
              if (
                missingFieldsList.some(
                  (mf) =>
                    mf.toLowerCase() === displayName.toLowerCase() ||
                    mf.toLowerCase() === nameWithoutDept.toLowerCase()
                )
              ) {
                shouldHaveBorder = true;
              }
            } else if (
              missingFieldsList.some(
                (mf) => mf.toLowerCase() === displayName.toLowerCase()
              )
            ) {
              shouldHaveBorder = true;
            }
          }
        }

        if (shouldHaveBorder) {
          cellElement.classList.add('missing-field');
          cellElement.style.border = '2px solid #f44336';
          cellElement.style.backgroundColor = '#ffebee';
        } else {
          cellElement.classList.remove('missing-field');
          cellElement.style.border = '';
          cellElement.style.backgroundColor = '';
        }
      });
    });
  };

  // Helper function to check if a lane is partially filled
  const isLanePartiallyFilled = (lane) => {
    const missing = getMissingFields(lane);
    return missing !== '';
  };

  const validateRequiredFields = () => {
    // If no tenderData, return true (no validation needed)
    if (!tenderData || tenderData.length === 0) {
      return true;
    }

    // Check that no lane is partially filled
    for (const lane of tenderData) {
      if (isLanePartiallyFilled(lane)) {
        return false;
      }
    }

    return true;
  };

  const fetchRequiredDocumentStatus = async (tenderId) => {
    if (!tenderId || user.isAdmin || user.isViewer) {
      setMissingRequiredDocuments([]);
      return [];
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/tender/${tenderId}/documents/`
      );
      const docs = response.data || [];
      const requiredParents = docs.filter(
        (doc) => !doc.parent_document && doc.requires_reupload
      );
      const missingParents = requiredParents.filter(
        (parent) =>
          !docs.some(
            (child) =>
              child.parent_document === parent.id &&
              child.uploaded_by_username === props.user
          )
      );

      setMissingRequiredDocuments(
        missingParents.map((doc) => doc.original_filename)
      );
      return missingParents.map((doc) => doc.original_filename);
    } catch (error) {
      console.warn('Could not load tender documents:', error);
      setMissingRequiredDocuments([]);
      return [];
    }
  };

  async function loadTenderData(tenderID) {
    // Exit fullscreen before switching to detail mode for the same reason
    // as the searchParams useEffect above: keeps handle node refs non-null
    // when fullscreenchange fires so handles don't spuriously become active.
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (_) {}
    }
    setTender(tenderID);
    setIsLoading(true);
    setTenderState(true);

    // Track tender view
    trackTenderView(tenderID).then((viewData) => {
      showDeclinedOfferNotification(viewData);
    });

    try {
      const url_api = API_URL_LANES_PRICING + tenderID + '/' + props.user;
      const { data } = await axios.get(url_api);
      setCompany(data.tender[0].company);
      // Use requested_capacity_text if available (including empty string), otherwise fall back to requested_capacity
      // Only set if there's meaningful content (not empty string)
      const capacityText =
        data.tender[0].requested_capacity_text !== undefined
          ? data.tender[0].requested_capacity_text
          : data.tender[0].requested_capacity;
      setRequestedCapacity(
        capacityText && capacityText.trim() ? capacityText : ''
      );
      setTenderInformation(data.tender[0].tender_information || '');
      //SET IT HERE
      setTenderStatus(data.tender[0].tender_status);
      await fetchRequiredDocumentStatus(tenderID);

      // Fetch custom columns to check which ones are required
      let customColumnsRequired = [];
      try {
        const customColumnsResponse = await axios.get(
          `${API_BASE_URL}/api/tender/custom-columns/${tenderID}`
        );

        const requiredCols = customColumnsResponse.data.filter(
          (col) => col.required
        );

        customColumnsRequired = requiredCols.map(
          (col) => col.column_ref || col.name
        );
      } catch (error) {
        console.warn('Could not fetch custom columns:', error);
      }

      const requiredFields = [
        ...(data.tender[0].capacity_required ? ['capacity'] : []),
        ...(data.tender[0].additional_costs_required
          ? ['additional_costs']
          : []),
        ...(data.tender[0].transit_time_required ? ['transit_time'] : []),
        ...(data.tender[0].additional_data_required ? ['additional_data'] : []),
        ...customColumnsRequired, // Add required custom columns
      ];
      setTenderRequiredFields(requiredFields);

      // Add Missing field to each lane row using the calculated required fields
      // Only calculate missing fields for department users, not admins or viewers
      const dataWithMissing = data.rows.map((lane) => {
        const missing = (user.isAdmin || user.isViewer)
          ? ''
          : getMissingFieldsForLane(lane, requiredFields);
        return {
          ...lane,
          missing: missing,
        };
      });
      setTenderData(dataWithMissing);
      let allPricesSet = true;
      data.rows.forEach((row) => {
        const value = row.final;
        let isFinalPriceSet = value != null && !isNaN(value) && value > 0;
        if (!isFinalPriceSet) {
          allPricesSet = false;
        }
      });
      setPricesCompleted(allPricesSet);

      // Check if all required fields are completed
      setRequiredFieldsComplete(validateRequiredFields());

      const url_api_checks = API_URL_TENDERS + tenderID;
      let data_checks = await axios.get(url_api_checks);
      data_checks = data_checks.data['0'];

      // Check if internal deadline exists and is past
      if (data_checks.internalDeadline) {
        setInternalDeadline(data_checks.internalDeadline);
        const deadlineDate = new Date(data_checks.internalDeadline);
        const currentDate = new Date();
        setIsPastDeadline(currentDate > deadlineDate);
      } else {
        setInternalDeadline(null);
        setIsPastDeadline(false);
      }

      // update pricing checkbox
      let status = data.tender[0].tender_status;

      if (
        data_checks.legal_check &&
        data_checks.finance_check &&
        data_checks.qsu_check
      ) {
        setChecksComplete(true);
      } else {
        setChecksComplete(false);
      }

      let header = await headerColumns(tenderID, { capacityText }, false, user?.isViewer);

      // Remove cell editing for viewers — they have read-only access
      if (props.disableCellEditing) {
        header = removeEditingFromColumns(header);
      }

      setGridCols(header);
      let headerF = await setGridColsFeedback(tenderID);
      if (props.disableCellEditing) {
        headerF = removeEditingFromColumns(headerF);
      }
      setGridColsF(applySavedColumnLayout(headerF, getColumnLayoutScope(tenderID)));
    } finally {
      setIsLoading(false);
    }

    formData = {};
    formDataLaneProperties = {};
  }

  const getTenderData = (params) => {
    setUrlParams(params.data.id);
  };

  // Unconfirm all prices for the current user and tender
  const unconfirmPrices = async (tenderId) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/tender/unconfirm-prices/${tenderId}/${props.user}`
      );
      return true;
    } catch (error) {
      console.error('Error unconfirming prices:', error);
      alert('An error occurred while unconfirming prices.');
      return false;
    }
  };

  // Handle dialog confirmation
  const handleConfirmUnconfirm = async () => {
    const success = await unconfirmPrices(tenders);
    if (success && pendingEdit) {
      // Proceed with the original edit
      processCellValueChange(pendingEdit.params);
      // Update the regionalStatus in the existing tenderData1 to prevent future confirmation dialogs
      setTenderData1((prevData) =>
        prevData.map((tender) =>
          tender.id === parseInt(tenders)
            ? { ...tender, regionalStatus: false }
            : tender
        )
      );
    }
    setShowConfirmDialog(false);
    setPendingEdit(null);
  };

  // Handle dialog cancellation
  const handleCancelUnconfirm = () => {
    if (pendingEdit) {
      // Revert the cell change
      const gridApi = pendingEdit.params.api;
      gridApi.refreshCells({
        force: true,
        rowNodes: [pendingEdit.params.node],
        columns: [pendingEdit.params.column.colId],
      });
    }
    setShowConfirmDialog(false);
    setPendingEdit(null);
  };

  // Check if prices are confirmed based on tender row data
  const isPricesConfirmed = (tenderRow) => {
    return tenderRow && tenderRow.regionalStatus === true;
  };

  const normalizeCellValue = (value, fallbackValue) => {
    const isInvalidNumberValue = (v) =>
      typeof v === 'number' && (!Number.isFinite(v) || Number.isNaN(v));

    let normalizedValue = value;

    // Some numeric editors can yield NaN which would be serialized as null in JSON.
    if (isInvalidNumberValue(normalizedValue)) {
      normalizedValue = fallbackValue;
    }

    if (
      normalizedValue === '' ||
      normalizedValue === undefined ||
      normalizedValue === null
    ) {
      return null;
    }

    if (typeof normalizedValue === 'number') {
      return Number.isFinite(normalizedValue) ? normalizedValue : null;
    }

    if (typeof normalizedValue === 'string') {
      const trimmedValue = normalizedValue.trim();
      return trimmedValue === '' ? null : trimmedValue;
    }

    return normalizedValue;
  };

  // Process the actual cell value change
  const processCellValueChange = (params) => {
    // If past the internal deadline, prevent cell value changes
    if (isPastDeadline) {
      // Revert any changes attempted
      const gridApi = params.api;
      gridApi.refreshCells({
        force: true,
        rowNodes: [params.node],
        columns: [params.column.colId],
      });
      // Show alert to the user
      alert('Price editing is disabled as the internal deadline has passed.');
      return;
    }

    const field = params.colDef.field;
    const lanePropertyFields = [
      'equipment',
      'payload',
      'transitTime',
      'requirements',
      'registerSheet',
      'oneway',
      'customerLaneID',
    ];

    // Skip processing for the missing field (it's computed, not saved)
    if (field === 'missing') {
      return;
    }

    // Check if this is a lane property field
    if (lanePropertyFields.includes(field)) {
      // Only allow superusers/admins to edit lane properties
      if (!user.isAdmin) {
        // Revert the change
        params.node.setDataValue(field, params.oldValue);
        return;
      }

      // Handle lane property changes
      let newData = formDataLaneProperties[params.data.id];
      if (!Boolean(newData)) {
        newData = {};
      }
      newData[field] = params.newValue;
      formDataLaneProperties[params.data.id] = newData;

      if (field === 'oneway') {
        const updatedFields = scaleRoundtripDependentFields(
          params.data,
          params.oldValue,
          params.newValue
        );

        if (updatedFields.length > 0) {
          params.api.refreshCells({
            force: true,
            rowNodes: [params.node],
            columns: updatedFields,
          });
        }
      }

      // Post lane property changes immediately
      postLaneProperties();
      return;
    }

    // Handle price changes (existing logic)
    setChangesDone(true);

    const currentCellValue = params?.node?.data?.[field];
    const processedValue = normalizeCellValue(
      params.newValue,
      currentCellValue
    );

    if (params.colDef.field.indexOf('customer_rel') >= 0) {
      formDataCustomerRel[params.data.id] = { value: processedValue };
    } else if (params.colDef.field.indexOf('customer_abs') >= 0) {
      formDataCustomerAbs[params.data.id] = { value: processedValue };
    } else {
      formData[params.data.id] = { value: processedValue };
    }
    let allPricesSet = true;
    let gridApi = params.api;
    gridApi.forEachNodeAfterFilterAndSort((node) => {
      const value = node?.data?.final;
      let isFinalPriceSet = value != null && !isNaN(value) && value > 0;
      if (!isFinalPriceSet) {
        allPricesSet = false;
      }
    });
    setPricesCompleted(allPricesSet);
    setChangesDoneStatus(allPricesSet);

    // Check if all required fields are completed (for UI state only)
    const isValidAfterChange = validateRequiredFields();
    // console.log("Validation after cell change:", isValidAfterChange, "for field:", params.colDef.field);
    setRequiredFieldsComplete(isValidAfterChange);

    // Update the missing field for the changed row if it's a required field
    const requiredFieldNames = [
      'regional',
      'capacity',
      'transit_time',
      'additional_costs',
      'additionalData_y',
    ];
    if (
      requiredFieldNames.includes(field) ||
      (field && field.startsWith('custom_'))
    ) {
      const updatedMissing = getMissingFields(params.data);
      params.data.missing = updatedMissing;

      // Refresh the missing column
      params.api.refreshCells({
        rowNodes: [params.node],
        columns: ['missing'],
        force: true,
      });

      // Use requestAnimationFrame twice to ensure AG Grid has completed rendering
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const rowIndex = params.node.rowIndex;
          const rowElement = document.querySelector(
            `[row-index="${rowIndex}"]`
          );
          if (rowElement) {
            const isPartiallyFilled =
              updatedMissing && updatedMissing.trim() !== '';

            // Update row background
            if (isPartiallyFilled) {
              rowElement.classList.add('partially-filled-lane');
            } else {
              rowElement.classList.remove('partially-filled-lane');
            }

            // Update the Missing column cell styling
            const missingCellElement =
              rowElement.querySelector(`[col-id="missing"]`);
            if (missingCellElement) {
              if (isPartiallyFilled) {
                missingCellElement.style.backgroundColor = '#ffebee';
                missingCellElement.style.border = '1px solid #f44336';
              } else {
                missingCellElement.style.backgroundColor = '';
                missingCellElement.style.border = '';
              }
            }

            // Update individual cell borders for required fields
            const missingFieldsList = updatedMissing
              .split(', ')
              .map((f) => f.trim())
              .filter((f) => f);

            // Get all cells in the row
            const allCells = rowElement.querySelectorAll('[col-id]');
            allCells.forEach((cellElement) => {
              const colId = cellElement.getAttribute('col-id');
              let shouldHaveBorder = false;

              if (isPartiallyFilled) {
                // Check standard required fields
                if (
                  colId === 'regional' &&
                  missingFieldsList.includes('price')
                ) {
                  shouldHaveBorder = true;
                } else if (
                  colId === 'capacity' &&
                  missingFieldsList.includes('capacity')
                ) {
                  shouldHaveBorder = true;
                } else if (
                  colId === 'transit_time' &&
                  missingFieldsList.includes('transit time')
                ) {
                  shouldHaveBorder = true;
                } else if (
                  colId === 'additional_costs' &&
                  missingFieldsList.includes('additional costs')
                ) {
                  shouldHaveBorder = true;
                } else if (
                  colId === 'additionalData_y' &&
                  missingFieldsList.includes('additional data')
                ) {
                  shouldHaveBorder = true;
                }
                // Check custom columns (they start with "custom_" or could be department-specific)
                else if (
                  colId &&
                  (colId.startsWith('custom_') || colId.includes('_mapped'))
                ) {
                  // Extract the display name from the column ID
                  let displayName = colId
                    .replace('custom_', '')
                    .replace(/_mapped$/, '')
                    .replace(/^\d+_/, '');
                  // Also check for department-specific columns (pattern: custom_X_Name_DepartmentName)
                  const parts = displayName.split('_');
                  if (parts.length > 1) {
                    // Try both the full name and the name without department suffix
                    const nameWithoutDept = parts.slice(0, -1).join('_');
                    if (
                      missingFieldsList.some(
                        (mf) =>
                          mf.toLowerCase() === displayName.toLowerCase() ||
                          mf.toLowerCase() === nameWithoutDept.toLowerCase()
                      )
                    ) {
                      shouldHaveBorder = true;
                    }
                  } else if (
                    missingFieldsList.some(
                      (mf) => mf.toLowerCase() === displayName.toLowerCase()
                    )
                  ) {
                    shouldHaveBorder = true;
                  }
                }
              }

              if (shouldHaveBorder) {
                cellElement.classList.add('missing-field');
                // Also set inline styles to ensure they override AG Grid's styles
                cellElement.style.border = '2px solid #f44336';
                cellElement.style.backgroundColor = '#ffebee';
              } else {
                cellElement.classList.remove('missing-field');
                // Remove inline styles
                cellElement.style.border = '';
                cellElement.style.backgroundColor = '';
              }
            });
          }
        });
      });
    }

    // Always proceed with network request for individual cell changes
    // Validation should only prevent "Confirm Prices" action, not individual updates
    postPrices(params.colDef.field);
  };

  const onCellEditingStopped = (params) => {
    // Show validation error if a transit_time value was typed but rejected by valueSetter
    if (
      params.valueChanged === false &&
      params.newValue !== null &&
      params.newValue !== undefined &&
      params.newValue !== ''
    ) {
      const field = params.colDef?.field || '';
      if (field === 'transit_time' || field.startsWith('transitTime_')) {
        const type = params.colDef?.transitTimeType || 'days';
        let message;
        if (type === 'intervals') {
          message = `Invalid transit time. Expected format: single letter dash single letter (e.g. A-B, A-C).`;
        } else {
          message = `Invalid transit time. Expected format: positive number (e.g. 2, 4.5).`;
        }
        setTransitTimeValidationError({ open: true, message });
      }
    }
  };

  const onCellValueChanged = async (params) => {
    // Check if user is regional (staff) and prices are confirmed
    // We need to find the current tender's regionalStatus from tenderData1
    if (user.isStaff && tenders) {
      const currentTender = tenderData1.find(
        (tender) => tender.id === parseInt(tenders)
      );
      if (currentTender && isPricesConfirmed(currentTender)) {
        // Store the pending edit and show confirmation dialog
        setPendingEdit({ params });
        setShowConfirmDialog(true);
        return;
      }
    }

    // Proceed with normal processing
    processCellValueChange(params);
  };

  const postPrices = async (field) => {
    let pricetype;
    if (props.price_type && tenderStatus === 'Round 1 Prices') {
      pricetype = props.price_type;
    } else if (props.price_type && tenderStatus === 'Round 2 Prices') {
      pricetype = props.price_type + '-2';
    } else if (props.price_type && tenderStatus === 'Round 3 Prices') {
      pricetype = props.price_type + '-3';
    } else if (tenderStatus === 'Round 2 Prices') {
      pricetype = props.price_type_r2;
    } else if (tenderStatus === 'Round 3 Prices') {
      pricetype = props.price_type_r3;
    } else if (tenderStatus === 'Round 1 Prices') {
      pricetype = props.price_type;
    } else {
      pricetype = '';
    }
    if (field === 'final') {
      pricetype = 'final';
    }
    if (field === 'capacity') {
      pricetype = 'capacity';
    }
    if (field === 'transit_time') {
      pricetype = 'transit_time';
    }
    if (field === 'additional_costs') {
      pricetype = 'additional_costs';
    }
    if (field === 'margin' || field.startsWith('margin - ')) {
      pricetype = 'margin';
    }
    if (field === 'additionalData_y') {
      pricetype = 'additionalData_y';
    }
    // Handle custom columns - extract the base custom column name from department-specific field
    if (field.startsWith('custom_') && field.includes('_')) {
      // Field format: custom_0_kaubatyyp_test -> extract custom_0_kaubatyyp
      const parts = field.split('_');
      if (parts.length >= 3) {
        const customColumnName = field.endsWith('_mapped')
          ? field.slice(0, -'_mapped'.length)
          : parts.slice(0, -1).join('_');
        pricetype = customColumnName;
      }
    }

    if (
      tenderStatus === 'Round 2 Feedback' ||
      tenderStatus === 'Round 3 Feedback'
    ) {
      let priceData = {
        customer_rel: formDataCustomerRel,
        customer_abs: formDataCustomerAbs,
      };
      let statusH = (await axios.get(API_URL_STATUS + tenders)).data;

      try {
        await axios
          .post(
            API_URL_PRICES_UPDATE +
              'customerFeedback' +
              statusH +
              '/' +
              tenders +
              '/' +
              props.user,
            priceData
          )
          .then((res) => console.log('Tender Price Update posted', res.data));
        formDataCustomerRel = {};
        formDataCustomerAbs = {};
      } catch (error) {
        if (error.response && error.response.status === 403) {
          alert(
            error.response.data.detail ||
              'Operation not allowed due to internal deadline.'
          );
          // Refresh the grid to revert any local changes
          gridRef2.current.api.refreshCells({ force: true });
        } else {
          console.error('Error updating customer feedback:', error);
          alert('An error occurred while updating customer feedback.');
          // Refresh the grid to revert any local changes
          gridRef2.current.api.refreshCells({ force: true });
        }
      }
    } else {
      if (field === 'empty-km') {
        const key = Object.keys(formData)[0];
        if (key) {
          formData[key]['key'] = 'empty-km';
        }
      }

      // Handle department-specific margin fields
      if (field.startsWith('margin - ')) {
        const key = Object.keys(formData)[0];
        if (key) {
          const departmentName = field.substring('margin - '.length);
          formData[key]['key'] = 'margin';
          formData[key]['department'] = departmentName;
        }
      }

      // Handle department-specific custom column fields
      if (
        field.startsWith('custom_') &&
        field.includes('_') &&
        !field.endsWith('_mapped')
      ) {
        const parts = field.split('_');
        if (parts.length >= 3) {
          const key = Object.keys(formData)[0];
          if (key) {
            const departmentName = parts[parts.length - 1]; // Last part is department name
            const customColumnName = parts.slice(0, -1).join('_'); // Everything except last part
            formData[key]['key'] = customColumnName;
            formData[key]['department'] = departmentName;
          }
        }
      }

      try {
        await axios
          .post(
            API_URL_PRICES_UPDATE +
              encodeURIComponent(pricetype) +
              '/' +
              tenders +
              '/' +
              props.user,
            formData
          )
          .then((res) => console.log('Tender Price Update posted', res.data));
      } catch (error) {
        if (error.response && error.response.status === 403) {
          alert(
            error.response.data.detail ||
              'Operation not allowed due to internal deadline.'
          );
          // Refresh the grid to revert any local changes
          gridRef1.current.api.refreshCells({ force: true });
        } else {
          console.error('Error updating prices:', error);
          alert('An error occurred while updating prices.');
          // Refresh the grid to revert any local changes
          gridRef1.current.api.refreshCells({ force: true });
        }
      }
    }
    setChangesDone(false);
    formData = {};
    formDataLaneProperties = {};
  };

  const confirmPrices = async () => {
    // Force any active cell editing to complete before confirming prices
    if (gridRef1.current && gridRef1.current.api) {
      gridRef1.current.api.stopEditing();
    }
    if (gridRef2.current && gridRef2.current.api) {
      gridRef2.current.api.stopEditing();
    }
    // If past the internal deadline, prevent price confirmation
    if (isPastDeadline) {
      alert(
        'Price submission is disabled as the internal deadline has passed.'
      );
      return;
    }

    // Ensure required documents are uploaded before confirming prices
    const missingDocs = await fetchRequiredDocumentStatus(tenders);
    if (missingDocs.length > 0) {
      alert(
        `Please upload your filled versions for the required documents before confirming prices: ${missingDocs.join(', ')}`
      );
      return;
    }

    // Check if all required fields are filled - perform fresh validation
    const currentValidationState = validateRequiredFields();
    if (!currentValidationState) {
      alert(
        `Some lanes are partially filled. Please either fill all required fields (${getRequiredFieldsDescription(
          tenderRequiredFields
        )}) for each lane or leave the lane completely empty before confirming prices.`
      );
      return;
    }

    const submitData = {};
    submitData['tenderID'] = tenders;

    const updateData = [];
    for (const lane of tenderData) {
      updateData.push(lane.id);
    }
    submitData['prices_for_lanes'] = updateData;
    submitData['user'] = props.user;

    try {
      await axios
        .post(`${API_BASE_URL}/api/tender/confirm-prices/`, submitData)
        .then((res) => console.log('Tender State Update posted', res.data));

      // Refresh the tender data to show updated status
      reset();
    } catch (error) {
      if (error.response && error.response.status === 403) {
        alert(
          error.response.data.detail ||
            'Operation not allowed due to internal deadline.'
        );
      } else {
        console.error('Error confirming prices:', error);
        alert('An error occurred while confirming prices.');
      }
    }

    //reset();
  };

  const postTenderStateUpdate = async (pushData, errorMessage) => {
    try {
      const res = await axios.post(API_URL_STATE_UPDATE, pushData);
      console.log('Tender State Update posted', res.data);
      reset();
      return true;
    } catch (error) {
      console.error(errorMessage, error);
      alert(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          'An error occurred while updating the tender state.'
      );
      return false;
    }
  };

  const rejectDepartmentTender = async () => {
    let comment = '';
    while (!comment) {
      const input = window.prompt(
        'Enter rejection reason for your department (required):',
        ''
      );

      if (input === null) {
        return;
      }

      comment = input.trim();
      if (!comment) {
        alert('A rejection reason is required to decline this tender.');
      }
    }

    if (
      !window.confirm(
        'Do you really want to reject this tender for your department?'
      )
    ) {
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/tender/reject-department/`, {
        tender_id: tenders,
        username: props.user,
        comment,
      });
      reset();
    } catch (error) {
      console.error('Error rejecting tender for department:', error);
      alert(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          'An error occurred while rejecting the tender for your department.'
      );
    }
  };

  const updateState = async () => {
    // Force any active cell editing to complete before confirming prices
    if (gridRef1.current && gridRef1.current.api) {
      gridRef1.current.api.stopEditing();
    }
    if (gridRef2.current && gridRef2.current.api) {
      gridRef2.current.api.stopEditing();
    }

    let pushData = {};
    let changes = { round2: { value: null } };

    if (checked === 'advance2' && tenderStatus === 'Round 1 Finalized') {
      changes.round2.value = 'Round 2 Feedback';
    } else if (checked === 'close0' && tenderStatus === 'Round 1 Finalized') {
      changes.round2.value = 'Closed';
    } else if (checked === 'advance3' && tenderStatus === 'Round 2 Finalized') {
      changes.round2.value = 'Round 3 Feedback';
    } else if (checked === 'close0' && tenderStatus === 'Round 2 Finalized') {
      changes.round2.value = 'Closed';
    } else if (checked === 'accept0') {
      changes.round2.value = 'Accepted';
    } else {
      changes.round2.value = 'Closed';
    }
    pushData['changesBackend'] = changes;
    pushData['tenderID'] = tenders;
    if (
      tenderStatus === 'Round 1 Finalized' ||
      tenderStatus === 'Round 2 Finalized'
    ) {
      pushData['changesBackend']['prices'] = {
        value: 'False',
        type: 'advance_round2',
      };
    }
    if (
      window.confirm(
        "Do you really want to advance the tender to status '" +
          changes['round2']['value'] +
          "'?"
      )
    ) {
      await postTenderStateUpdate(pushData, 'Error advancing tender state');
    }
  };

  const closeTender = async () => {
    // Force any active cell editing to complete before confirming prices
    if (gridRef1.current && gridRef1.current.api) {
      gridRef1.current.api.stopEditing();
    }
    if (gridRef2.current && gridRef2.current.api) {
      gridRef2.current.api.stopEditing();
    }

    let pushData = {};
    let changes = { round2: { value: 'Closed' } };
    pushData['changesBackend'] = changes;
    pushData['tenderID'] = tenders;
    if (window.confirm('Do you really want to close the tender?')) {
      await postTenderStateUpdate(pushData, 'Error closing tender');
    }
  };

  const activateTender = async () => {
    // Force any active cell editing to complete before confirming prices
    if (gridRef1.current && gridRef1.current.api) {
      gridRef1.current.api.stopEditing();
    }
    if (gridRef2.current && gridRef2.current.api) {
      gridRef2.current.api.stopEditing();
    }

    let pushData = {};
    let changes = { round2: { value: 'Round 1 Prices' } };
    pushData['changesBackend'] = changes;
    pushData['tenderID'] = tenders;
    pushData['reactivate'] = 'True';
    pushData['user'] = tenderStatus;
    const isDraftTender = tenderStatus === 'Draft';
    const confirmationMessage = isDraftTender
      ? 'Do you really want to publish the tender to Pricing Round 1?'
      : 'Do you really want to reactivate the tender?';
    if (window.confirm(confirmationMessage)) {
      await postTenderStateUpdate(
        pushData,
        isDraftTender ? 'Error publishing tender' : 'Error reactivating tender'
      );
    }
  };

  const deleteTender = async () => {
    // Force any active cell editing to complete before confirming prices
    if (gridRef1.current && gridRef1.current.api) {
      gridRef1.current.api.stopEditing();
    }
    if (gridRef2.current && gridRef2.current.api) {
      gridRef2.current.api.stopEditing();
    }

    let delData = { tender_id: tenders };
    if (window.confirm('Do you really want to delete the tender?')) {
      if (window.confirm('Are you really sure?')) {
        await axios
          .post(API_URL_TENDERS_DELETE, delData)
          .then((res) => console.log('Tender Deleted', res.data));
        reset();
      }
    }
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      // group columns configured to use the Group Column Filter
      filter: '`Company Name`',
    };
  }, []);

  const postLaneProperties = async () => {
    if (Object.keys(formDataLaneProperties).length === 0) {
      return;
    }

    try {
      const api_url = API_URL_EDIT + 'lane';
      await axios.post(api_url, formDataLaneProperties);
      formDataLaneProperties = {}; // Clear the form data after successful post
    } catch (error) {
      console.error('Error updating lane properties:', error);
    }
  };

  const hasMissingRequiredDocuments = missingRequiredDocuments.length > 0;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {tenderState && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <Typography align="left" marginTop={'10px'}>
              Tender for Company '{company}' with ID {tenders} Selected!
            </Typography>

            {tenderInformation && (
              <Typography
                align="left"
                fontWeight={500}
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  marginTop: '8px',
                  border: '1px solid #ddd',
                }}
              >
                <strong>Tender Title:</strong> {tenderInformation}
              </Typography>
            )}

            {fillingNotes && notesAcknowledged && (
              <Typography
                align="left"
                fontWeight={500}
                sx={{
                  backgroundColor: '#e3f2fd',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  marginTop: '8px',
                  border: '1px solid #2196f3',
                  whiteSpace: 'pre-wrap',
                }}
              >
                <strong>Filling Instructions:</strong> {fillingNotes}
              </Typography>
            )}

            {props.customerContacts && (
              <Box sx={{ mt: 1 }}>
                {props.contactsError && (
                  <Alert severity="error" sx={{ mb: 1 }}>{props.contactsError}</Alert>
                )}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                    Customer Account Manager:
                  </Typography>
                  {props.contactsLoading && <CircularProgress size={14} />}
                  {!props.contactsLoading && props.customerContacts.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No Customer Account Manager configured.</Typography>
                  )}
                  {props.customerContacts.map((email) => (
                    <Chip key={email} label={email} size="small" variant="outlined" />
                  ))}
                  {props.onEditContacts && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={props.onEditContacts}
                      disabled={props.contactsLoading}
                    >
                      Edit
                    </Button>
                  )}
                </Stack>
              </Box>
            )}


            {isPastDeadline && (
              <Grid item xs={12}>
                <Typography
                  align="left"
                  fontWeight={600}
                  style={{
                    color: 'red',
                    backgroundColor: '#ffeeee',
                    padding: '10px',
                    borderRadius: '5px',
                    border: '1px solid red',
                    marginBottom: '10px',
                  }}
                >
                  <span style={{ fontWeight: 'bold' }}>
                    ⚠️ DEADLINE PASSED:
                  </span>{' '}
                  The internal deadline (
                  {new Date(internalDeadline).toLocaleString()}) has passed.
                  Price editing and submission are now disabled.
                </Typography>
              </Grid>
            )}
            <Grid item xs={10}></Grid>

            {tenderStatus !== 'Round 2 Feedback' &&
              tenderStatus.indexOf('Rejected') < 0 &&
              tenderStatus !== 'Round 3 Feedback' && (
                <FullScreen handle={fullScreenHandle1}>
                  <div
                    style={isFullscreen ? {
                      display: 'flex', flexDirection: 'column',
                      height: '100%', width: '100%',
                    } : {}}
                    ref={ref}
                    id="fullScreen-wrap"
                  >
                    <TableControls
                      table={gridRef1}
                      fullScreenHandle={fullScreenHandle1}
                      showImport={!user.isAdmin && !user.isViewer && tenderState}
                      showExport={true}
                      showExportDropdown={user.isAdmin || user.isViewer}
                      tenders={tenders}
                      user={props.user}
                      layoutScope={detailLayoutScope}
                    />
                    <div
                      style={isFullscreen ? {
                        flex: 1, minHeight: 0,
                      } : {
                        height: 720, width: '100%',
                        marginBottom: '40px', textAlign: 'center',
                      }}
                      className="ag-theme-alpine asdf"
                    >
                    <AgGridReact
                      autoGroupColumnDef={autoGroupColumnDef}
                      ref={gridRef1} // Ref for accessing Grid's API
                      suppressColumnVirtualisation={true} // Render all headers so auto-size can measure them
                      rowData={tenderData} // Row Data for Rows
                      columnDefs={gridCols} // Column Defs for Columns
                      onFirstDataRendered={() =>
                        autoSizeGridColumns(gridRef1, detailLayoutScope)
                      }
                      onGridColumnsChanged={() =>
                        autoSizeGridColumns(gridRef1, detailLayoutScope)
                      }
                      onColumnMoved={(event) =>
                        persistColumnLayout(event, detailLayoutScope)
                      }
                      onColumnPinned={(event) =>
                        persistColumnLayout(event, detailLayoutScope)
                      }
                      onColumnVisible={(event) =>
                        persistColumnLayout(event, detailLayoutScope)
                      }
                      onColumnResized={(event) =>
                        persistColumnLayoutOnResize(event, detailLayoutScope)
                      }
                      onCellValueChanged={
                        isPastDeadline ? null : onCellValueChanged
                      }
                      onCellEditingStopped={onCellEditingStopped}
                      suppressClickEdit={props.disableCellEditing || undefined}
                      gridOptions={{
                        ...gridOptions,
                        // Disable cell editing if past deadline - use default cell-level editing for immediate saves
                        editType: (isPastDeadline || props.disableCellEditing) ? 'none' : undefined,
                        // Make cells look disabled if past deadline
                        getRowStyle: isPastDeadline
                          ? () => ({ opacity: '0.7' })
                          : null,
                      }}
                      pagination={true}
                      paginationAutoPageSize={true}
                    />
                    </div>
                  </div>
                </FullScreen>
              )}

            {(tenderStatus === 'Round 2 Feedback' ||
              tenderStatus === 'Round 3 Feedback') && (
              <FullScreen handle={fullScreenHandle2}>
                <div style={isFullscreen ? {
                  display: 'flex', flexDirection: 'column',
                  height: '100%', width: '100%',
                } : {}}>
                  <TableControls
                    table={gridRef2}
                    fullScreenHandle={fullScreenHandle2}
                    showImport={!user.isAdmin && !user.isViewer && tenderState}
                    showExport={true}
                    showExportDropdown={user.isAdmin || user.isViewer}
                    tenders={tenders}
                    user={props.user}
                    layoutScope={detailLayoutScope}
                  />
                  <div
                    style={isFullscreen ? {
                      flex: 1, minHeight: 0,
                    } : {
                      height: 720, width: '100%',
                      marginBottom: '40px', textAlign: 'center',
                    }}
                    className="ag-theme-alpine"
                  >
                  <AgGridReact
                    ref={gridRef2} // Ref for accessing Grid's API
                    suppressColumnVirtualisation={true} // Render all headers so auto-size can measure them
                    rowData={tenderData} // Row Data for Rows
                    columnDefs={gridColsF} //laneColumnDefinitionRound2} // Column Defs for Columns
                    onFirstDataRendered={() =>
                      autoSizeGridColumns(gridRef2, detailLayoutScope)
                    }
                    onGridColumnsChanged={() =>
                      autoSizeGridColumns(gridRef2, detailLayoutScope)
                    }
                    onColumnMoved={(event) =>
                      persistColumnLayout(event, detailLayoutScope)
                    }
                    onColumnPinned={(event) =>
                      persistColumnLayout(event, detailLayoutScope)
                    }
                    onColumnVisible={(event) =>
                      persistColumnLayout(event, detailLayoutScope)
                    }
                    onColumnResized={(event) =>
                      persistColumnLayoutOnResize(event, detailLayoutScope)
                    }
                    onCellValueChanged={onCellValueChanged}
                    onCellEditingStopped={onCellEditingStopped}
                    suppressClickEdit={props.disableCellEditing || undefined}
                    gridOptions={{
                      ...gridOptions,
                      editType: props.disableCellEditing ? 'none' : undefined,
                    }}
                    pagination={true}
                    paginationAutoPageSize={true}
                  />
                  </div>
                </div>
              </FullScreen>
            )}
          </div>
        )}
      </div>

      {!tenderState && (
        <FullScreen handle={fullScreenHandle3}>
          <div
            style={isFullscreen ? {
              display: 'flex', flexDirection: 'column', height: '100%',
            } : {
              display: 'flex', flexDirection: 'column', gap: 16,
            }}
          >
            <Typography variant="h6" align="left">
              Tenders with Actions to be Done
            </Typography>
            <TableControls
              table={gridRef3}
              fullScreenHandle={fullScreenHandle3}
              showImport={false}
              showExport={true}
              showExportDropdown={user.isAdmin || user.isViewer}
              tenders={tenders}
              user={props.user}
              layoutScope={overviewLayoutScope}
            />
            <div
              style={isFullscreen ? {
                flex: 1, minHeight: 0,
              } : {
                height: 720, width: '100%',
                marginBottom: '40px', textAlign: 'center',
              }}
              className="ag-theme-alpine"
              onContextMenu={(e) => { if (user?.isAdmin) e.preventDefault(); }}
            >
              <AgGridReact
                ref={gridRef3} // Ref for accessing Grid's API
                suppressColumnVirtualisation={true} // Render all headers so auto-size can measure them
                rowData={tenderData1} // Row Data for Rows
                columnDefs={overviewGridCols} // Column Defs for Columns
                onFirstDataRendered={() =>
                  autoSizeGridColumns(gridRef3, overviewLayoutScope)
                }
                onGridColumnsChanged={() =>
                  autoSizeGridColumns(gridRef3, overviewLayoutScope)
                }
                onColumnMoved={(event) =>
                  persistColumnLayout(event, overviewLayoutScope)
                }
                onColumnPinned={(event) =>
                  persistColumnLayout(event, overviewLayoutScope)
                }
                onColumnVisible={(event) =>
                  persistColumnLayout(event, overviewLayoutScope)
                }
                onColumnResized={(event) =>
                  persistColumnLayoutOnResize(event, overviewLayoutScope)
                }
                onRowDoubleClicked={getTenderData}
                onCellContextMenu={user?.isAdmin ? handleCellContextMenu : undefined}
                suppressContextMenu={true}
                rowClassRules={{
                  'declined-offer-row': (params) =>
                    Boolean(params.data?.department_rejected),
                }}
                pagination={true}
                paginationAutoPageSize={true}
              />
            </div>
          </div>
        </FullScreen>
      )}

      {tenderState && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            marginTop: 16,
          }}
        >
          {!user.isAdmin &&
            !user.isViewer &&
            !isPastDeadline &&
            requiredFieldsComplete &&
            (() => {
              const currentTender = tenderData1.find(
                (tender) => tender.id === parseInt(tenders)
              );
              return !isPricesConfirmed(currentTender) && !currentTender?.department_rejected;
            })() && (
              <Typography align="right" variant="h6">
                You are done with the prices? Mark them as completed and send
                them to the central tender management!
              </Typography>
            )}
          {!user.isAdmin && !user.isViewer && !isPastDeadline && !requiredFieldsComplete && (
            <Typography align="right" variant="h6" style={{ color: 'orange' }}>
              Some lanes are partially filled. Please either complete all
              required fields (
              {getRequiredFieldsDescription(tenderRequiredFields)}) or leave
              lanes completely empty.
            </Typography>
          )}
          {!user.isAdmin && !user.isViewer && isPastDeadline && (
            <Typography align="right" variant="h6" style={{ color: 'red' }}>
              The internal deadline has passed. Price submission is now
              disabled.
            </Typography>
          )}

          {user.isAdmin &&
            (tenderStatus === 'Round 1 Finalized' ||
              tenderStatus === 'Round 2 Finalized' ||
              tenderStatus === 'Round 3 Finalized') && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  justifyContent: 'end',
                }}
              >
                <FormControl>
                  <RadioGroup
                    defaultValue="close"
                    value={checked.toString()}
                    onChange={handleChangeSwitch}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 8,
                    }}
                  >
                    <FormControlLabel
                      value="close0"
                      control={<Radio />}
                      label="Close Tender"
                    />

                    {tenderStatus === 'Round 1 Finalized' && (
                      <FormControlLabel
                        value="advance2"
                        control={<Radio />}
                        label="Advance Tender to Round 2"
                      />
                    )}
                    {tenderStatus === 'Round 2 Finalized' && (
                      <FormControlLabel
                        value="advance3"
                        control={<Radio />}
                        label="Advance Tender to Round 3"
                      />
                    )}
                    {(tenderStatus === 'Round 2 Finalized' ||
                      tenderStatus === 'Round 3 Finalized') &&
                      checksComplete && (
                        <FormControlLabel
                          value="accept0"
                          control={<Radio />}
                          label="Accept Tender"
                        />
                      )}
                  </RadioGroup>
                </FormControl>
                {(tenderStatus === 'Round 2 Finalized' ||
                  tenderStatus === 'Round 3 Finalized') &&
                  !checksComplete && (
                    <Typography>Not All Checks Completed</Typography>
                  )}
              </div>
            )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'end',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              {(tenderStatus === 'Round 1 Prices' ||
                tenderStatus === 'Round 2 Prices' ||
                tenderStatus === 'Round 3 Prices' ||
                tenderStatus === 'Round 2 Feedback' ||
                tenderStatus === 'Round 3 Feedback') &&
                props.user !== 'admin' &&
                props.user !== 'tender-management' &&
                !user.isViewer &&
                (() => {
                  const currentTender = tenderData1.find(
                    (tender) => tender.id === parseInt(tenders)
                  );
                  return !isPricesConfirmed(currentTender) && !currentTender?.department_rejected;
                })() && (
                  <Stack spacing={1} alignItems="flex-end">
                    {isPastDeadline ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        disabled
                        onClick={confirmPrices}
                        title="Internal deadline has passed"
                        sx={{ minWidth: 160 }}
                      >
                        Confirm Prices
                      </Button>
                    ) : hasMissingRequiredDocuments ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        disabled
                        onClick={confirmPrices}
                        title="Upload required documents before confirming prices"
                        sx={{ minWidth: 160 }}
                      >
                        Confirm Prices
                      </Button>
                    ) : !requiredFieldsComplete ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        disabled
                        onClick={confirmPrices}
                        title={`Some lanes are partially filled. Either fill all required fields (${getRequiredFieldsDescription(
                          tenderRequiredFields
                        )}) or leave the lane completely empty`}
                        sx={{ minWidth: 160 }}
                      >
                        Confirm Prices
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={confirmPrices}
                        sx={{ minWidth: 160 }}
                      >
                        Confirm Prices
                      </Button>
                    )}
                    {user.isStaff && (
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={rejectDepartmentTender}
                        color="error"
                        sx={{ minWidth: 160 }}
                      >
                        Reject Tender
                      </Button>
                    )}
                    {hasMissingRequiredDocuments && (
                      <Typography
                        variant="body2"
                        color="warning.main"
                        sx={{ maxWidth: 220, textAlign: 'right' }}
                      >
                        Upload required documents before confirming prices.
                      </Typography>
                    )}
                  </Stack>
                )}
              {tenderStatus === 'Accepted' && (
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={closeTender}
                  color="inherit"
                  style={{ alignSelf: 'end' }}
                >
                  Close Tender
                </Button>
              )}
              {tenderStatus === 'Closed' && (
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={deleteTender}
                  color="inherit"
                  style={{ alignSelf: 'end' }}
                >
                  Delete Tender
                </Button>
              )}
            </div>
            {tenderStatus.indexOf('Rejected') < 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'end',
                  gap: 8,
                }}
              >
                {user.isAdmin &&
                  (tenderStatus === 'Round 1 Finalized' ||
                    tenderStatus === 'Round 2 Finalized' ||
                    tenderStatus === 'Round 3 Finalized') && (
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={updateState}
                    >
                      {checked.substring(0, checked.length - 1)} Tender
                    </Button>
                  )}
                {user.isAdmin &&
                  (tenderStatus === 'Round 1 Prices' ||
                    tenderStatus === 'Round 2 Prices' ||
                    tenderStatus === 'Round 3 Prices' ||
                    tenderStatus === 'Round 2 Feedback' ||
                    tenderStatus === 'Round 3 Feedback') && (
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={closeTender}
                      color="inherit"
                    >
                      Close Tender
                    </Button>
                  )}
              </div>
            )}{' '}
          </div>
        </div>
      )}

      {tenderState &&
        !user.isViewer &&
        (tenderStatus === 'Draft' ||
          tenderStatus.indexOf('Rejected') >= 0 ||
          (user.isAdmin && tenderStatus === 'Closed')) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              gap: 8,
            }}
          >
            <Button
              variant="outlined"
              size="medium"
              onClick={activateTender}
              color="inherit"
              sx={{ mt: 1 }}
            >
              {tenderStatus === 'Draft'
                ? 'Publish Tender'
                : 'Reopen Tender'}
            </Button>

            {tenderStatus.indexOf('Rejected') >= 0 && (
              <Button
                variant="outlined"
                size="medium"
                onClick={deleteTender}
                color="inherit"
              >
                Delete Tender
              </Button>
            )}
          </div>
        )}

      {/* Price Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelUnconfirm}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Prices Already Confirmed
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            The prices for this tender have already been confirmed. To make
            changes, you need to unconfirm all prices first.
            <br />
            <br />
            Do you want to proceed with unconfirming the prices and making your
            changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUnconfirm} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUnconfirm}
            color="primary"
            variant="contained"
          >
            Yes, Unconfirm and Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filling Notes Dialog */}
      <FillingNotesDialog
        open={showFillingNotesDialog}
        onClose={() => setShowFillingNotesDialog(false)}
        onAcknowledge={acknowledgeFillingNotes}
        fillingNotes={fillingNotes}
        tenderInfo={{
          id: tenders,
          company: company,
        }}
      />
      <Snackbar
        open={declinedOfferNotification.open}
        autoHideDuration={7000}
        onClose={() =>
          setDeclinedOfferNotification((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() =>
            setDeclinedOfferNotification((current) => ({
              ...current,
              open: false,
            }))
          }
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {declinedOfferNotification.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={transitTimeValidationError.open}
        autoHideDuration={6000}
        onClose={handleTransitTimeValidationErrorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeTransitTimeValidationError}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {transitTimeValidationError.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={statusChangeError.open}
        autoHideDuration={8000}
        onClose={() => setStatusChangeError((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setStatusChangeError((s) => ({ ...s, open: false }))}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {statusChangeError.message}
        </Alert>
      </Snackbar>
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9998,
          }}
          onClick={closeContextMenu}
          onContextMenu={(e) => { e.preventDefault(); closeContextMenu(); }}
        />
      )}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: '#fff',
            border: '1px solid #d0d0d0',
            borderRadius: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            zIndex: 9999,
            minWidth: 200,
            padding: '4px 0',
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              padding: '4px 16px 6px',
              fontSize: 12,
              color: '#888',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottom: '1px solid #eee',
              marginBottom: 4,
            }}
          >
            Set Status
          </div>
          {TENDER_STATUSES.filter(s => s !== contextMenu.tender.tender_status).map(status => (
            <div
              key={status}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: 14,
              }}
              onClick={() => handleSetTenderStatus(contextMenu.tender, status)}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {status}
            </div>
          ))}
        </div>
      )}
    </Box>
  );
});
export default LanePricing;
